import { UA } from '../uas/ua.interface';
import { Uta } from '../uas/ua.model';
import { UaTranslation } from '../uas/ua-building-blocks';
import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { ApiService } from '@hub-services/api';
import { DatePipe } from '@angular/common';

@Injectable({ providedIn: 'root' })
export class Utasv1Service {
  path: string = '/api';
  constructor(
    private apiService: ApiService,
    private datePipe: DatePipe,
  ) { }

  successWord(): string {
    const words = ['Yay', 'Neat', 'Bravo', 'Cool', 'Nice', 'Super', 'Ooh', 'Great'];
    const random = Math.round(Math.random() * (words.length - 1));
    return words[random];
  }

  // Responder unwraps the `{data}` key, so this is a bare Uta[] (Resources::Uta — same as the detail).
  getUtasByStatus(airlineId: string, status: string): Observable<Uta[]> {
    return this.apiService.get<Uta[]>(`${this.path}/airlines/${airlineId}/utas?status=${status}`).pipe(
      map((response: Uta[]) => {
        return response;
      }),
    );
  }

  processUtaTranslations<T extends { translations: Partial<Record<string, UaTranslation & { translated?: boolean }>> }>(
    uta: T,
    languages: string[],
    defaultCode: string,
  ): T {
    const defaultTranslation = _.clone(uta.translations[defaultCode]);
    languages.forEach((code: string) => {
      const existing = uta.translations[code];
      if (!existing) {
        uta.translations[code] = defaultTranslation;
        if (defaultTranslation) {
          defaultTranslation.translated = false;
        }
      } else {
        existing.translated = true;
      }
    });
    return uta;
  }

  processUtasTranslations<T extends { translations: Partial<Record<string, UaTranslation & { translated?: boolean }>> }>(
    utas: T[],
    languages: string[],
    defaultCode: string,
  ): T[] {
    return utas.map((uta) => {
      const defaultTranslation = _.clone(uta.translations[defaultCode]);
      languages.forEach((code: string) => {
        // translations is Partial: a locale may be absent (Resources::Uta only emits existing
        // translations). Guard before dereferencing — missing is treated like an empty translation.
        const existing = uta.translations[code];
        if (!existing || existing.headline.length === 0) {
          uta.translations[code] = defaultTranslation;
          if (defaultTranslation) {
            defaultTranslation.translated = false;
          }
        } else {
          existing.translated = true;
        }
      });
      return uta;
    });
  }

  draftUa(ua: any) {
    return this.apiService
      .post(`${this.path}/utas/${ua.id}/status_transitions`, {
        data: {
          status: 'draft',
        },
      })
      .pipe(
        map(() => {
          ua.moved_title = `${this.successWord()}! Attribute ${ua.id} drafted.`;
          ua.moved_description = 'This Attribute will hang out in Draft until published.';
          ua.moved_path = '/utas/draft';
          return ua;
        }),
      );
  }

  submitUa(ua: any) {
    return this.apiService.post(`${this.path}/utas/${ua.id}/submit`, {}).pipe(
      map(() => {
        ua.moved_title = `${this.successWord()}! Attribute ${ua.id} submitted.`;
        ua.moved_description = 'This Attribute will hang out In review until published.';
        ua.moved_path = '/utas/pending';
        return ua;
      }),
    );
  }

  publishUa(ua: any) {
    return this.apiService.post<UA>(`${this.path}/utas/${ua.id}/publish`, {}).pipe(
      map((response) => {
        let suffix: string = 'and is now available everywhere';
        if (response.is_pending) {
          suffix = `but will wait In review until ${this.datePipe.transform(response.starts_on, 'MMMM d, yyyy')}`;
        }
        ua.moved_title = `${this.successWord()}! Attribute ${ua.id} submitted.`;
        ua.moved_description = `This Attribute has been published ${suffix}.`;
        ua.moved_path = '/utas/published';
        return ua;
      }),
    );
  }

  disableUa(ua: any) {
    return this.apiService.post(`${this.path}/utas/${ua.id}/disable`, {}).pipe(
      map(() => {
        ua.moved_title = `${this.successWord()}! Attribute ${ua.id} is taking a nap.`;
        ua.moved_description = 'This Attribute will remain Disabled until you enable it.';
        ua.moved_path = '/utas/disabled';
        return ua;
      }),
    );
  }

  enableUa(ua: any) {
    return this.apiService.post(`${this.path}/utas/${ua.id}/enable`, {}).pipe(
      map(() => {
        ua.moved_title = `${this.successWord()}! Attribute ${ua.id} is back in business.`;
        ua.moved_description = 'This Attribute has been published and is now available everywhere.';
        ua.moved_path = '/utas/published';
        return ua;
      }),
    );
  }

  cloneUa(ua: any, partnerId: any) {
    return this.apiService
      .post<UA>(`${this.path}/airlines/${partnerId}/utas/${ua.id}`, {
        data: {
          source_upa_id: ua.id,
        },
      })
      .pipe(
        map((response) => {
          return `/utas/${response.id}/basics`;
        }),
      );
  }

  deleteUa(ua: { id: number }): Observable<void> {
    return this.apiService.delete<void>(`${this.path}/utas/${ua.id}`);
  }

  // TODO(strict-typing): confirm response shape and replace UA with the exact
  // model. UA is the legacy god-object interface; only `uta.id` is read at the
  // single caller (utas-market-manager) so any narrowing that keeps `id: number`
  // will work. Widened here to unblock strictFunctionTypes without silently
  // dropping type coverage at the subscribe site (was `subscribe((uta: UA))`).
  createUta(partnerId: number, utaData: any): Observable<UA> {
    return this.apiService.post<UA>(`/api/airlines/${partnerId}/utas`, {
      data: utaData,
    });
  }

  updateUta(utaData: any) {
    return this.apiService.put(`${this.path}/utas/${utaData.id}`, { data: utaData }).pipe(
      map((response: any) => {
        return response;
      }),
    );
  }
}
